import type { ReactNode } from 'react';
import React, { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { SHORTCUT_REGISTRATION_ERROR } from '../constants';
import type { ShortcutRegistrationErrorT } from '../types';
import { CUSTOM_KEYBOARD_EVENTS, getSerializedKeys, isEventTriggeredFromEditableContainer, MODIFIER_KEYS_MAP, normalizeEventKey, resolvePlatformKeys, SUPPORTED_KEYBOARD_EVENTS } from './utils/shortcut';
type RegisterActions = (typeof SUPPORTED_KEYBOARD_EVENTS)[keyof typeof SUPPORTED_KEYBOARD_EVENTS] | (typeof CUSTOM_KEYBOARD_EVENTS)[keyof typeof CUSTOM_KEYBOARD_EVENTS];
type RegisterHotKeyActionT = {
  type?: RegisterActions;
  keys: string[];
  onAction: (e: KeyboardEvent) => void;
  allowInEditable?: boolean;
};
type RegisterHotkeysArgsT = RegisterHotKeyActionT[];
type ShortcutStoreT = {
  registerHotKeys: (args: RegisterHotkeysArgsT) => {
    errors: ShortcutRegistrationErrorT[];
    removeHotKeys: () => void;
  };
};
export const ShortcutContext = createContext<ShortcutStoreT>({
  registerHotKeys: (_args: RegisterHotkeysArgsT) => {
    return {
      errors: [],
      removeHotKeys: () => {}
    };
  }
});
type PropsT = {
  children: ReactNode;
};
type HotkeyActionHandlerT = {
  onAction: (event: KeyboardEvent) => void;
  allowInEditable?: boolean;
};
export const ShortcutProvider = ({
  children
}: PropsT) => {
  const hotkeyMapRef = useRef<Map<RegisterActions, Map<string, HotkeyActionHandlerT>>>(new Map());
  const timerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const handleEventList = useCallback(event => {
    const eventList = hotkeyMapRef.current.get(event.type);
    if (!eventList) return;
    const eventKeys = Object.keys(MODIFIER_KEYS_MAP).reduce((acc, modifierKey) => {
      const key = MODIFIER_KEYS_MAP[modifierKey];
      return event[key] ? [...acc, modifierKey] : acc;
    }, [normalizeEventKey(event)]);
    const eventKeysSerializedKeys = getSerializedKeys(eventKeys);
    const handler = eventKeysSerializedKeys ? eventList.get(eventKeysSerializedKeys) : undefined;
    if (!handler) return;
    const inEditable = isEventTriggeredFromEditableContainer(event.target);
    if (inEditable && !handler.allowInEditable) return;
    handler.onAction(event);
  }, []);
  const handleLongPressList = useCallback(event => {
    /*
        longpress event type is called  separately as it's not a default event
        this is  created  for our use case to show shortcut on toolbar when longpressed ctrl
        longpress is used separately because it is not a event .it is a long press keydown.
    */

    const longPressList = hotkeyMapRef.current.get(CUSTOM_KEYBOARD_EVENTS.LONG_PRESS);
    const {
      type: eventType,
      key: eventKey
    } = event;
    const handler = longPressList?.get?.(eventKey);
    const activeLongPressEventId = timerRef.current.get(eventKey);
    if (!handler) return;
    const inEditable = isEventTriggeredFromEditableContainer(event.target);
    if (inEditable && !handler.allowInEditable) return;
    if (eventType === SUPPORTED_KEYBOARD_EVENTS.KEYDOWN && !activeLongPressEventId) {
      timerRef.current.set(eventKey, setTimeout(() => {
        handler.onAction(event);
        timerRef.current.delete(event.key);
      }, 800));
    }
    if ([SUPPORTED_KEYBOARD_EVENTS.KEYUP, SUPPORTED_KEYBOARD_EVENTS.VISIBILITY_CHANGE].includes(eventType)) {
      handler.onAction(event);
      clearTimeout(activeLongPressEventId);
      timerRef.current.delete(eventKey);
    }
  }, []);
  const handleToggleList = useCallback(event => {
    const {
      key: eventKey,
      type: eventType
    } = event;
    const toggleList = hotkeyMapRef.current.get(CUSTOM_KEYBOARD_EVENTS.TOGGLE);
    const handler = toggleList?.get?.(eventKey);
    if (!handler) return;
    const inEditable = isEventTriggeredFromEditableContainer(event.target);
    if (inEditable && !handler.allowInEditable) return;
    if ([SUPPORTED_KEYBOARD_EVENTS.KEYDOWN, SUPPORTED_KEYBOARD_EVENTS.KEYUP].includes(eventType)) {
      handler.onAction(event);
    }
  }, []);
  const eventHandler = useCallback(event => {
    handleEventList(event);
    handleLongPressList(event);
    handleToggleList(event);
  }, [handleEventList, handleLongPressList, handleToggleList]);
  useEffect(() => {
    const SUPPORTED_EVENTS_KEYS = Object.keys(SUPPORTED_KEYBOARD_EVENTS);
    SUPPORTED_EVENTS_KEYS.forEach(key => {
      window.addEventListener(SUPPORTED_KEYBOARD_EVENTS[key], eventHandler);
    });
    return () => {
      SUPPORTED_EVENTS_KEYS.forEach(key => {
        window.removeEventListener(SUPPORTED_KEYBOARD_EVENTS[key], eventHandler);
      });
    };
  }, [eventHandler]);
  const registerHotKeys = useCallback((shortcutKeys: RegisterHotkeysArgsT): {
    errors: ShortcutRegistrationErrorT[];
    removeHotKeys: () => void;
  } => {
    const keyRegistryErrors: ShortcutRegistrationErrorT[] = [];
    const errorKeys: string[] = [];
    shortcutKeys.forEach(({
      type = SUPPORTED_KEYBOARD_EVENTS.KEYDOWN,
      keys,
      onAction,
      allowInEditable
    }) => {
      const platformKeys = resolvePlatformKeys(keys);
      const serializedKey = getSerializedKeys(platformKeys);
      if (!hotkeyMapRef.current.get(type)) {
        hotkeyMapRef.current.set(type, new Map());
      }
      const eventList = hotkeyMapRef.current.get(type);
      if (!eventList?.has(serializedKey)) {
        eventList?.set?.(serializedKey, {
          onAction,
          allowInEditable
        });
      } else {
        errorKeys.push(serializedKey);
        keyRegistryErrors.push({
          type: SHORTCUT_REGISTRATION_ERROR.TYPE,
          errorKey: keys.join(),
          message: SHORTCUT_REGISTRATION_ERROR.MESSAGE
        });
      }
    });
    return {
      errors: keyRegistryErrors,
      removeHotKeys: () => {
        shortcutKeys.forEach(({
          type = SUPPORTED_KEYBOARD_EVENTS.KEYDOWN,
          keys
        }) => {
          const serializedKey = getSerializedKeys(resolvePlatformKeys(keys));
          if (errorKeys.includes(serializedKey)) return;
          const eventList = hotkeyMapRef.current.get(type);
          eventList?.delete?.(serializedKey);
        });
      }
    };
  }, []);
  const value = useMemo(() => ({
    registerHotKeys
  }), [registerHotKeys]);
  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>;
};